import { useState } from 'react';
import { AlertTriangle, Info, Loader2, X, Wrench, Siren, Banknote, Receipt, ListChecks } from 'lucide-react';
import { useCancelContract, useRefundablePayment } from '@/features/contracts/hooks/useContracts';
import { formatCents } from '@/features/contracts/utils/refundLabels';
import { formatPrice } from '@/features/contracts/utils/contractFormatter';
import { billedServiceTtc } from '@/features/contracts/utils/billedAmount';
import { getSavStatusLabel, getSavStatusColorClass } from '@/utils/savStatusHelpers';
import {
  ASSISTANCE_LABEL,
  assistanceCancellationNotice,
  assistanceFormulaWording,
} from '@/features/assistance/assistanceWording';
import { useAssistanceCancellationImpact } from '@/features/assistance/hooks/useAssistanceCancellationImpact';

const CANCELLATION_REASON_LABELS: Record<string, string> = {
  input_error: 'Erreur de saisie',
  client_withdrawal: 'Désistement client',
  other_warranty: 'Autre garantie souhaitée par le client',
  other: 'Autre',
};

interface CancelContractModalProps {
  contractId: number;
  contractLabel: string;
  hasSignature: boolean;
  hasPayment: boolean;
  hasInvoice: boolean;
  /** Prestation Affinicar seule, pour chiffrer l'avoir. */
  servicePrice?: number | null;
  /** Supplément d'assistance : il s'AJOUTE à la prestation dans l'avoir. */
  assistancePrice?: number | null;
  cancelationReason?: string | null;
  cancelationDetails?: string | null;
  savs?: Array<{ id: number; uniqueId?: string; status: number; dateCreation: string }>;
  /** Snapshot d'assistance du contrat (`null` = aucune). */
  assistanceFormulaId?: number | null;
  assistanceLabel?: string | null;
  assistanceBenefits?: Record<string, unknown> | null;
  isOpen: boolean;
  onClose: () => void;
}

export function CancelContractModal({
  contractId,
  contractLabel,
  hasSignature,
  hasPayment,
  hasInvoice,
  servicePrice,
  assistancePrice,
  cancelationReason,
  cancelationDetails,
  savs,
  assistanceFormulaId,
  assistanceLabel,
  assistanceBenefits,
  isOpen,
  onClose,
}: CancelContractModalProps) {
  const { mutate, isLoading } = useCancelContract();

  // Interrogé seulement quand la modale est ouverte ET que le contrat porte une
  // assistance : inutile de poser la question dans tous les autres cas.
  const hasAssistance = assistanceFormulaId != null;
  const { data: assistanceImpact } = useAssistanceCancellationImpact(
    contractId,
    isOpen && hasAssistance,
  );

  // Remboursement du dernier paiement au client : proposé seulement si le contrat a un
  // paiement Stripe. Toutes les cases sont cochées par défaut — l'annulation rend ce
  // qui a été pris, et c'est à l'utilisateur de décocher pour s'en écarter.
  const {
    data: refundable,
    isInitialLoading: isCheckingRefund,
    isError: refundCheckFailed,
  } = useRefundablePayment(contractId, isOpen && hasPayment);
  const [refund, setRefund] = useState(true);
  const [refundCommission, setRefundCommission] = useState(true);
  const [createAvoir, setCreateAvoir] = useState(true);

  // Le hook doit être appelé avant tout retour anticipé — règle des hooks React.
  if (!isOpen) return null;

  const refundablePayment = refundable?.payment ?? null;
  const willRefund = !!refundablePayment && refund;
  const commissionCents = refundablePayment?.commissionCents ?? 0;
  const willReturnCommission = willRefund && commissionCents > 0 && refundCommission;

  // Un avoir n'existe que si le contrat a été porté sur une facture : sans elle il
  // n'y a rien à créditer, et il n'y aura jamais rien — la facturation exclut les
  // contrats annulés.
  const willCreateAvoir = hasInvoice && createAvoir;
  const avoirTtc = billedServiceTtc(servicePrice, assistancePrice);

  const assistanceWording = hasAssistance
    ? assistanceFormulaWording(assistanceBenefits, assistanceLabel)
    : null;
  // Règle de `resolveCancelInsurer` côté API : on ne réclame la prime à l'assisteur
  // que si le vendeur ne paiera pas la prestation. Les deux doivent dire la même
  // chose — c'est la même décision, annoncée ici et prise là-bas.
  const willClaimPremium = !hasInvoice || willCreateAvoir;
  const assistanceNotice = assistanceCancellationNotice(assistanceImpact, willClaimPremium);

  const handleClose = () => {
    setRefund(true);
    setRefundCommission(true);
    setCreateAvoir(true);
    onClose();
  };

  const handleConfirm = () => {
    mutate(
      {
        id: contractId,
        skipAvoir: !createAvoir,
        refund: willRefund,
        refundCommission: willReturnCommission,
      },
      {
        onSuccess: () => handleClose(),
      },
    );
  };

  const reasonLabel = cancelationReason ? (CANCELLATION_REASON_LABELS[cancelationReason] || cancelationReason) : null;

  /**
   * Ce qui va réellement se produire, dans l'ordre où le moteur l'exécute.
   *
   * Recalculé à chaque changement de case : l'utilisateur lit les conséquences de
   * SES réponses, pas une liste générique. Une annulation est irréversible et touche
   * trois tiers — le client, le vendeur, l'assisteur ; le détail de ce qui part chez
   * chacun ne doit pas se découvrir après coup.
   */
  const recapitulatif: string[] = [];
  recapitulatif.push("Le contrat passe au statut Annulé, à la date d'aujourd'hui.");
  if (hasSignature) {
    recapitulatif.push('La signature électronique est annulée chez YouSign.');
  }
  if (hasPayment) {
    recapitulatif.push(
      "Le paiement ou l'abonnement Stripe est annulé — un prélèvement déjà encaissé ou en cours ne peut pas l'être.",
    );
  }
  if (willRefund && refundablePayment) {
    recapitulatif.push(
      `Remboursement de ${formatCents(refundablePayment.amountCents)} au client. Il part dès que le solde Stripe du vendeur le couvre ; ses virements sont suspendus en attendant et le client est prévenu par email.`,
    );
  } else if (refundablePayment) {
    recapitulatif.push(
      `Aucun remboursement : le dernier paiement de ${formatCents(refundablePayment.amountCents)} reste acquis.`,
    );
  } else if (hasPayment && !isCheckingRefund) {
    recapitulatif.push('Aucun paiement à rembourser au client.');
  }
  if (willRefund && commissionCents > 0) {
    recapitulatif.push(
      willReturnCommission
        ? `Affinicar rend sa commission de ${formatCents(commissionCents)} au vendeur, une fois le remboursement effectif.`
        : `Affinicar conserve sa commission de ${formatCents(commissionCents)}.`,
    );
  }
  if (hasInvoice) {
    recapitulatif.push(
      willCreateAvoir
        ? `Un avoir de ${formatPrice(avoirTtc)} TTC est créé au vendeur.`
        : "Aucun avoir : la prestation reste facturée au vendeur.",
    );
  } else {
    recapitulatif.push(
      "Le contrat n'a pas été facturé au vendeur, et ne le sera pas : la facturation exclut les contrats annulés.",
    );
  }
  if (hasAssistance && assistanceNotice) {
    recapitulatif.push(assistanceNotice);
  }
  if (savs && savs.length > 0) {
    recapitulatif.push(
      `Les ${savs.length} SAV associés ne sont pas clôturés : ils restent à traiter séparément.`,
    );
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      {/*
        `max-h` + `overflow-hidden` + colonne flex : sans plafond, un panneau plus
        haut que la fenêtre est centré par le flex parent, donc son haut sort par
        le dessus — hors d'atteinte, et rien ne défile puisque l'overlay est
        `fixed`. Seul le corps défile : en-tête et pied restent visibles, et les
        boutons d'action sont dans le pied.
      */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-lg tw-w-full tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-200 tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <AlertTriangle className="tw-w-5 tw-h-5 tw-text-red-500" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
              Annuler le contrat
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/*
          Corps : le contexte, puis les questions, puis les conséquences. L'ordre
          suit la chaîne de l'argent — le client, le vendeur, l'assisteur — et la
          question de l'avoir précède l'encart d'assistance, qui en dépend.
        */}
        <div className="tw-p-5 tw-space-y-4 tw-flex-1 tw-overflow-y-auto">
          <p className="tw-text-sm tw-text-gray-600 tw-m-0">
            Vous allez annuler le contrat <strong>{contractLabel}</strong>.
            Cette action est irréversible.
          </p>

          {/* Demande d'annulation du prescripteur */}
          {reasonLabel && (
            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-md tw-p-3">
              <div className="tw-flex tw-items-start tw-gap-2">
                <Info className="tw-w-4 tw-h-4 tw-text-blue-600 tw-flex-shrink-0 tw-mt-0.5" />
                <div>
                  <p className="tw-text-sm tw-font-medium tw-text-blue-800 tw-m-0 tw-mb-1">
                    Demande d'annulation du prescripteur
                  </p>
                  <p className="tw-text-sm tw-text-blue-700 tw-m-0">
                    Raison : <strong>{reasonLabel}</strong>
                  </p>
                  {cancelationDetails && (
                    <p className="tw-text-sm tw-text-blue-700 tw-m-0 tw-mt-1">
                      Détails : {cancelationDetails}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {savs && savs.length > 0 && (
            <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-md tw-p-3">
              <div className="tw-flex tw-items-start tw-gap-2">
                <Wrench className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                <div className="tw-flex-1">
                  <p className="tw-text-sm tw-font-medium tw-text-amber-800 tw-m-0 tw-mb-2">
                    Ce contrat a {savs.length} SAV associé{savs.length > 1 ? 's' : ''}
                  </p>
                  <div className="tw-space-y-1.5">
                    {savs.map((sav) => (
                      <div key={sav.id} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-amber-700">
                        <span className="tw-font-medium">{sav.uniqueId ? `SAV-${sav.uniqueId}` : `#${sav.id}`}</span>
                        <span className={`tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full ${getSavStatusColorClass(sav.status)}`}>
                          {getSavStatusLabel(sav.status)}
                        </span>
                        <span className="tw-text-xs tw-text-amber-600">
                          {new Date(sav.dateCreation).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="tw-text-xs tw-text-amber-600 tw-m-0 tw-mt-2">
                    L'annulation du contrat n'annulera pas les SAV associés.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* QUESTION 1 — rembourser le client */}
          {hasPayment && (
            <div className="tw-border tw-border-gray-200 tw-rounded-md tw-p-3">
              <div className="tw-flex tw-items-start tw-gap-2">
                <Banknote className="tw-w-4 tw-h-4 tw-text-gray-600 tw-flex-shrink-0 tw-mt-0.5" />
                <div className="tw-flex-1 tw-space-y-2">
                  {isCheckingRefund && (
                    <p className="tw-text-sm tw-text-gray-500 tw-m-0 tw-flex tw-items-center tw-gap-2">
                      <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                      Recherche du dernier paiement…
                    </p>
                  )}

                  {!isCheckingRefund && refundCheckFailed && (
                    <p className="tw-text-sm tw-text-amber-700 tw-m-0">
                      Impossible de vérifier les paiements Stripe : aucun remboursement ne sera demandé.
                    </p>
                  )}

                  {!isCheckingRefund && !refundCheckFailed && !refundablePayment && (
                    <p className="tw-text-sm tw-text-gray-600 tw-m-0">Aucun paiement à rembourser au client.</p>
                  )}

                  {refundablePayment && (
                    <>
                      <label className="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-select-none">
                        <input
                          type="checkbox"
                          checked={refund}
                          onChange={(e) => setRefund(e.target.checked)}
                          disabled={isLoading}
                          className="tw-w-4 tw-h-4 tw-mt-0.5 tw-accent-affinicar-primary tw-cursor-pointer"
                        />
                        <span className="tw-text-sm tw-text-gray-900">
                          Rembourser au client le dernier paiement :{' '}
                          <strong>{formatCents(refundablePayment.amountCents)}</strong> du{' '}
                          {new Date(refundablePayment.paymentDate).toLocaleDateString('fr-FR')} (
                          {refundablePayment.status === 'processing' ? 'prélèvement en cours' : 'encaissé'})
                        </span>
                      </label>

                      {refundable && refundable.paymentsCount > 1 && (
                        <div className="tw-flex tw-items-start tw-gap-2 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-md tw-p-2">
                          <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                          <p className="tw-text-sm tw-text-amber-800 tw-m-0">
                            Ce contrat a {refundable.paymentsCount} paiements : seul le dernier sera remboursé.
                          </p>
                        </div>
                      )}

                      {refund && (
                        <>
                          {refundablePayment.status === 'processing' && (
                            <p className="tw-text-xs tw-text-gray-600 tw-m-0">
                              Le prélèvement est encore en cours de traitement : il sera remboursé dès son encaissement.
                            </p>
                          )}

                          {commissionCents > 0 && (
                            <label className="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-select-none">
                              <input
                                type="checkbox"
                                checked={refundCommission}
                                onChange={(e) => setRefundCommission(e.target.checked)}
                                disabled={isLoading}
                                className="tw-w-4 tw-h-4 tw-mt-0.5 tw-accent-affinicar-primary tw-cursor-pointer"
                              />
                              <span className="tw-text-sm tw-text-gray-900">
                                Rendre la commission Affinicar au vendeur ({formatCents(commissionCents)})
                              </span>
                            </label>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/*
            QUESTION 2 — l'avoir au vendeur. Posée comme les autres, et non par un
            second bouton de validation : un choix qui change ce qui est facturé ne
            doit pas se faire en visant le bon bouton. Formulée positivement, pour
            que cocher veuille dire faire.
          */}
          {hasInvoice && (
            <div className="tw-border tw-border-gray-200 tw-rounded-md tw-p-3">
              <div className="tw-flex tw-items-start tw-gap-2">
                <Receipt className="tw-w-4 tw-h-4 tw-text-gray-600 tw-flex-shrink-0 tw-mt-0.5" />
                <div className="tw-flex-1 tw-space-y-2">
                  <label className="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-select-none">
                    <input
                      type="checkbox"
                      checked={createAvoir}
                      onChange={(e) => setCreateAvoir(e.target.checked)}
                      disabled={isLoading}
                      className="tw-w-4 tw-h-4 tw-mt-0.5 tw-accent-affinicar-primary tw-cursor-pointer"
                    />
                    <span className="tw-text-sm tw-text-gray-900">
                      Créer un avoir au vendeur : <strong>{formatPrice(avoirTtc)} TTC</strong>
                    </span>
                  </label>
                  <p className="tw-text-xs tw-text-gray-600 tw-m-0">
                    {createAvoir
                      ? "Ce contrat a été facturé au vendeur : l'avoir lui rend la prestation" +
                        (hasAssistance ? ' et le supplément d\'assistance.' : '.')
                      : 'Ce contrat a été facturé au vendeur, et le restera : il paiera cette prestation.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/*
            Conséquence chez l'assisteur, APRÈS la question de l'avoir dont elle
            dépend : la prime n'est réclamée que si le vendeur ne paie pas la
            prestation. Ni avertissement ni perte, d'où la teinte maison plutôt que
            l'ambre ou le rouge des encarts voisins.
          */}
          {hasAssistance && (
            <div className="tw-bg-affinicar-lighter tw-border tw-border-affinicar-light tw-rounded-md tw-p-3">
              <div className="tw-flex tw-items-start tw-gap-2">
                <Siren className="tw-w-4 tw-h-4 tw-text-affinicar-secondary tw-flex-shrink-0 tw-mt-0.5" />
                <div className="tw-flex-1">
                  <p className="tw-text-sm tw-font-medium tw-text-affinicar-secondary tw-m-0 tw-mb-1">
                    {ASSISTANCE_LABEL}
                    {assistanceWording ? ` — ${assistanceWording}` : ''}
                  </p>
                  {assistanceNotice && (
                    <p className="tw-text-sm tw-text-gray-700 tw-m-0">{assistanceNotice}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* RÉCAPITULATIF — ce qui part vraiment, une fois les réponses données. */}
          <div className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-rounded-md tw-p-3">
            <div className="tw-flex tw-items-start tw-gap-2">
              <ListChecks className="tw-w-4 tw-h-4 tw-text-gray-700 tw-flex-shrink-0 tw-mt-0.5" />
              <div className="tw-flex-1">
                <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-m-0 tw-mb-2">
                  En confirmant, voici ce qui sera fait
                </p>
                <ul className="tw-text-sm tw-text-gray-700 tw-m-0 tw-pl-5 tw-space-y-1">
                  {recapitulatif.map((ligne) => (
                    <li key={ligne}>{ligne}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-5 tw-border-t tw-border-gray-200 tw-flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || isCheckingRefund}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
            Confirmer l'annulation
          </button>
        </div>
      </div>
    </div>
  );
}
